import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminActions,
  AdminLegendVariant,
  ContractTypes,
  getPharmacyDetails,
  getPharmacyWorkshiftsList,
  MiscType,
  PermissionsOfAdmin,
  PharmacyFilterWorkshifts,
  PharmacyLegendVariant,
  pharmacyWorkshiftsList,
  resetStatus,
  successOrSelector,
  successSelector,
  userPreferredTimeFormat,
} from '@pharmaplan/common';
import type {
  IAdminBookedProfessional,
  ICalendarEventType,
} from '@pharmaplan/common';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import { IDynamicTableObject } from '../../../components/DynamicTable/types';
import {
  pastWorkshiftsListHeaders,
  pharmacyWorkshiftsListHeaders,
  pharmacyWorkshiftsListRow,
} from '../../../components/Admin/PharmacyList/ListOfWorkshifts/helper';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../../localization';
import useWorkshiftDelete, {
  IShowCancellationParams,
} from '../useWorkshiftDelete';
import useUserProfileLink from '../useUserProfileLink';
import useAdminPermissions from '../useAdminPermissions';
import {
  ListOfWorkshiftsTabTypes,
  MomentTimeFormats,
  OtherScreens,
  ScreenTypes,
} from '../../../helpers/Constants';
import {
  renderScreen,
  resetDrawer,
  setOpen,
} from '../../../actions/drawerActions';
import useDrawer from '../../useDrawer';
import useAdminReportsFn from '../../AdminReports/useAdminReportsFn';
import useTableSelectCheckbox from '../../useTableSelectCheckbox';
import useTableSort from '../../useTableSort';
import { setDialog } from '../../../reducers/dialogReducer';
import UserSelectionModal from '../../../components/UserSelectionModal';
import ViewOtherRequestedOffers from '../../../components/SelfService/ViewOtherRequestOffers';
import UpdateWorkshiftsModal from '../../../components/Admin/PharmacyList/ListOfWorkshifts/UpdateWorkshiftsModal';

const { bookAvailablity: bookAvailablityAction, expressBooking } = adminActions;
const { booked, requested, posted } = AdminLegendVariant;
const cancelActions = [
  adminActions.adminCancelBooking,
  adminActions.adminCancelMultiple,
];

const handleAll = (value: PharmacyFilterWorkshifts | MiscType) =>
  (value === MiscType.All ? '' : value);

const handleShiftType = (value: ContractTypes | MiscType) =>
  (value === MiscType.All ? null : value as ContractTypes);

interface IWorkshiftListFilters {
  desc?: boolean;
  endDate?: DateTime | null;
  past?: boolean;
  replacementTypes?: Array<number>;
  shiftType?: ContractTypes | null;
  sortBy?: string;
  startDate?: DateTime | null;
  status?: PharmacyFilterWorkshifts | string;
}

export interface IWorkshiftListFormValues {
  endDate: DateTime | null;
  replacementTypes: Array<number>;
  shiftType: ContractTypes | MiscType;
  startDate: DateTime | null;
  status: PharmacyFilterWorkshifts | MiscType;
}

const formatDateFilter = (date: DateTime | null) =>
  date?.toFormat(MomentTimeFormats.fullDate) ?? null;

const useListOfWorkshifts = (pharmacyId: string) => {
  const [page, setPage] = useState(1);
  const [selectedCount, setSelectedCount] = useState(0);
  const [appliedShiftType, setAppliedShiftType] = useState<
    ContractTypes | null
  >(null);
  const [selectedTab, setSelectedTab] = useState<ListOfWorkshiftsTabTypes>(
    ListOfWorkshiftsTabTypes.upcomingWorkshifts,
  );

  const dispatch = useAppDispatch();
  const { goToUserProfile } = useUserProfileLink();
  const { can } = useAdminPermissions();
  const {
    showWorkshiftDelete,
    showRequestCancellation,
    showMultipleWorkshiftsDelete,
  } = useWorkshiftDelete();

  const handleCancellation = ({
    longTermShiftContractId,
    bookingId,
    dateTime,
    bookedProfessionals,
  }: IShowCancellationParams & {
    bookedProfessionals: Array<IAdminBookedProfessional>;
  }) => {
    if (longTermShiftContractId && bookedProfessionals.length > 1) {
      dispatch(
        setDialog({
          heading: {
            title: '',
          },
          Component: (
            <UserSelectionModal
              bookedProfessionals={bookedProfessionals}
              longTermShiftContractId={longTermShiftContractId}
            />
          ),
          showCloseButton: true,
        }),
      );
    } else {
      showRequestCancellation({
        bookingId,
        dateTime,
        longTermShiftContractId,
        professionalId: bookedProfessionals?.[0]?.professionalId,
      });
    }
  };

  const { openDrawer } = useDrawer();
  const { findMatchingAvailabilities } = useAdminReportsFn();

  const { order, orderBy, handleSorting, isDesc } = useTableSort();

  const workshifts = useAppSelector(pharmacyWorkshiftsList);
  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const deleteSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        adminActions.deletePharmacyWorkshift,
        adminActions.adminDeleteContract,
        adminActions.deleteMultipleWorkshift,
      ],
      state,
    ));
  const editSuccess = useAppSelector((state) =>
    successOrSelector(
      [adminActions.editAdminWorkshift, adminActions.adminUpdateShiftContract],
      state,
    ));
  const bookingAvailabilitySuccess = useAppSelector((state) =>
    successSelector([bookAvailablityAction], state));
  const expressBookingSuccess = useAppSelector((state) =>
    successSelector([expressBooking], state));
  const cancelBookingSuccess = useAppSelector((state) =>
    successOrSelector(cancelActions, state));
  const updateRateAllowancesSuccess = useAppSelector((state) =>
    successSelector([adminActions.updateWorkshiftRateAllowances], state));

  const canDelete = can(PermissionsOfAdmin.Delete);
  const isPast = selectedTab === ListOfWorkshiftsTabTypes.pastWorkshifts;

  const { data, totalCount } = workshifts ?? {};

  const selectableData = useMemo(() => {
    const skipCriteria = [
      PharmacyLegendVariant.booked,
      PharmacyLegendVariant.requested,
    ];
    return data
      .filter(
        (item) =>
          !skipCriteria.includes(item.type as PharmacyLegendVariant),
      )
      .map((item) =>
        item.workShiftId);
  }, [data]);

  const [multiDayShiftIds, setMultiDayShiftIds] = useState<Array<string>>([]);

  useEffect(() => {
    const pageMultiDayShiftIds = data
      .filter((item) =>
        !!item.longTermShiftContractId)
      .map((item) =>
        item.workShiftId);

    setMultiDayShiftIds((prev) => {
      const merged = [...new Set([...prev, ...pageMultiDayShiftIds])];

      return merged.length === prev.length ? prev : merged;
    });
  }, [data]);

  const {
    handlePress,
    selectedIds,
    selectAll,
    allIds,
    handleSelectedIds,
    handleSelectAll,
    handleReset: resetSelectedIds,
  } = useTableSelectCheckbox({
    page,
    dataLength: selectableData.length,
    useOfEdit: false,
    userType: MiscType.All,
  });

  const resetSelectedWorkshifts = useCallback(() => {
    resetSelectedIds();
    setSelectedCount(0);
  }, [resetSelectedIds]);

  const getWorkshiftList = useCallback((
    selectedPage = 1,
    {
      status = '',
      past = isPast,
      replacementTypes = [],
      shiftType = null,
      startDate = null,
      endDate = null,
      sortBy,
      desc,
    }: IWorkshiftListFilters = {},
  ) => {
    dispatch(
      getPharmacyWorkshiftsList({
        desc,
        endDate: formatDateFilter(endDate),
        page: selectedPage,
        pharmacyId,
        past,
        replacementTypes,
        shiftType,
        sortBy,
        startDate: formatDateFilter(startDate),
        status,
      }),
    );
  }, [dispatch, isPast, pharmacyId]);

  const onSubmit = (values: IWorkshiftListFormValues) => {
    const {
      endDate,
      replacementTypes,
      shiftType,
      startDate,
      status,
    } = values;
    const normalizedShiftType = handleShiftType(shiftType);

    setPage(1);
    setAppliedShiftType(normalizedShiftType);
    resetSelectedWorkshifts();
    getWorkshiftList(1, {
      endDate,
      replacementTypes,
      shiftType: normalizedShiftType,
      startDate,
      status: handleAll(status),
    });
  };

  const formik = useFormik<IWorkshiftListFormValues>({
    initialValues: {
      endDate: null,
      status: MiscType.All,
      replacementTypes: [],
      shiftType: MiscType.All,
      startDate: null,
    },
    onSubmit,
  });

  const { values, resetForm, handleSubmit } = formik ?? {};
  const {
    endDate,
    replacementTypes,
    shiftType,
    startDate,
    status,
  } = values;
  const currentFilters = useMemo<IWorkshiftListFilters>(() =>
    ({
      endDate,
      replacementTypes,
      shiftType: handleShiftType(shiftType),
      startDate,
      status: handleAll(status),
      ...(orderBy ? { desc: isDesc, sortBy: orderBy } : {}),
    }), [
    endDate,
    isDesc,
    orderBy,
    replacementTypes,
    shiftType,
    startDate,
    status,
  ]);

  const handleFilterReset = () => {
    resetForm();
    resetSelectedWorkshifts();
    setAppliedShiftType(null);
    setPage(1);
    getWorkshiftList(1);
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const gotoPage = selectedPage + 1;
    setPage(gotoPage);
    getWorkshiftList(gotoPage, currentFilters);
  };

  const showExpressBooking = (
    workshiftId: string,
    longTermShiftContractId?: string,
    type?: AdminLegendVariant,
  ) => {
    openDrawer();
    dispatch(
      renderScreen({
        screenNumber: 5,
        screenType: ScreenTypes.availableWorkshift,
        eventId: workshiftId ?? '',
        workshiftId,
        extraParameters: {
          longTermShiftContractId,
        },
        fromScreen: OtherScreens.ListOfWorkshifts,
        type: type ?? posted,
      }),
    );
  };

  const handleEdit = (type: ICalendarEventType, bookingId: string | null, workshiftId: string) =>
    () => {
      openDrawer();

      switch (type) {
        case booked:
          dispatch(
            renderScreen({
              screenNumber: 5,
              screenType: ScreenTypes.availableWorkshift,
              eventId: bookingId ?? '',
              workshiftId,
              type,
              fromScreen: OtherScreens.ListOfWorkshifts,
            }),
          );
          return;
        case requested:
          showExpressBooking(workshiftId);
          return;
        case posted:
          dispatch(
            renderScreen({
              screenNumber: 2,
              screenType: ScreenTypes.availableWorkshift,
              eventId: workshiftId,
              fromScreen: OtherScreens.ListOfWorkshifts,
            }),
          );
          break;
        default:
          break;
      }
    };

  const handleWorkshiftDelete = () => {
    showMultipleWorkshiftsDelete(allIds, selectedCount);
  };

  const selectedWorkshiftCount = allIds.length;
  const hasSelectedWorkshifts = selectedWorkshiftCount > 0;
  const hasMultiDaySelected = useMemo(() =>
    allIds.some((id) =>
      multiDayShiftIds.includes(id)), [allIds, multiDayShiftIds]);
  const canUpdateMultiple = hasSelectedWorkshifts && !hasMultiDaySelected;

  const handleWorkshiftUpdate = () => {
    if (!canUpdateMultiple) {
      return;
    }

    dispatch(
      setDialog({
        Component: (
          <UpdateWorkshiftsModal
            pharmacyId={pharmacyId}
            shiftIds={allIds}
          />
        ),
        maxWidth: 'sm',
        showCloseButton: false,
      }),
    );
  };

  const customSelectAll = (toBeSet: boolean) => {
    let ids: Array<string> = [];

    if (toBeSet) {
      ids = selectableData;
      handleSelectedIds({ [page]: ids });
    } else {
      handleSelectedIds({ [page]: [] });
    }

    handleSelectAll({ [page]: toBeSet });
  };

  const customHandlePress = (id: string, count?: number) =>
    () => {
      if (selectedIds.includes(id)) {
        setSelectedCount((prev) =>
          prev - (count || 1));
      } else {
        setSelectedCount((prev) =>
          prev + (count || 1));
      }

      handlePress(id)();
    };

  const showRequestedProfessionals = ({
    workshiftId,
    longTermShiftContractId,
  }: {
    workshiftId: string;
    professionalId?: string;
    longTermShiftContractId?: string;
  }) => {
    if (longTermShiftContractId) {
      dispatch(
        setDialog({
          heading: {
            title: strings.bookingRequests,
          },
          Component: (
            <ViewOtherRequestedOffers
              workshiftId={workshiftId}
              longTermId={longTermShiftContractId}
            />
          ),
          maxWidth: 'md',
          showCloseButton: true,
        }),
      );
    } else {
      dispatch(setOpen(true));
      dispatch(
        renderScreen({
          screenNumber: 2,
          screenType: ScreenTypes.requestedList,
          eventId: workshiftId,
        }),
      );
    }
  };

  const table: IDynamicTableObject = {
    headerBar: [],
    title: strings.activePharmacies,
    headers: isPast
      ? pastWorkshiftsListHeaders()
      : pharmacyWorkshiftsListHeaders(),
    rows: pharmacyWorkshiftsListRow({
      data,
      timeFormat,
      goToUserProfile,
      showWorkshiftDelete,
      findMatchingAvailabilities,
      canDelete,
      isPast,
      showRequestedProfessionals,
      showRequestCancellation: handleCancellation,
      handleEdit,
      showExpressBooking,
      selectedItems: selectedIds,
      handlePress: customHandlePress,
      disableActions: hasSelectedWorkshifts,
    }),
  };

  useEffect(() => {
    dispatch(getPharmacyDetails(pharmacyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetForm();
    resetSelectedWorkshifts();
    setAppliedShiftType(null);
    setPage(1);
    getWorkshiftList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(
        resetStatus([
          adminActions.deletePharmacyWorkshift,
          adminActions.deleteMultipleWorkshift,
        ]),
      );
      showSuccess(dispatch, strings.workshiftDeletedSuccessfully);
      resetForm();
      getWorkshiftList();
      resetSelectedWorkshifts();
      setAppliedShiftType(null);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  // SELECTION IS KEPT AFTER A BULK UPDATE SO THE USER STILL SEES WHICH
  // SHIFTS WERE UPDATED
  useEffect(() => {
    if (updateRateAllowancesSuccess) {
      dispatch(resetStatus([adminActions.updateWorkshiftRateAllowances]));
      getWorkshiftList(page, {
        ...currentFilters,
        shiftType: appliedShiftType,
      });
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
    }
  }, [
    appliedShiftType,
    currentFilters,
    dispatch,
    getWorkshiftList,
    page,
    updateRateAllowancesSuccess,
  ]);

  useEffect(() => {
    if (bookingAvailabilitySuccess || expressBookingSuccess) {
      getWorkshiftList(page, currentFilters);
      dispatch(resetStatus([bookAvailablityAction, expressBooking]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingAvailabilitySuccess, expressBookingSuccess]);

  useEffect(() => {
    if (cancelBookingSuccess) {
      handleSubmit();
      dispatch(resetStatus(cancelActions));
      showSuccess(dispatch, strings.bookingCancelledSuccessfully);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelBookingSuccess]);

  useEffect(() => {
    if (editSuccess) {
      dispatch(
        resetStatus([
          adminActions.editAdminWorkshift,
          adminActions.adminUpdateShiftContract,
        ]),
      );
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      resetForm();
      setAppliedShiftType(null);
      setPage(1);
      getWorkshiftList(1);
      dispatch(resetDrawer());
    }
  }, [dispatch, editSuccess, getWorkshiftList, resetForm]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      getWorkshiftList(1, { ...currentFilters, desc: isDesc, sortBy: orderBy });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return {
    table,
    page,
    handlePagination,
    totalCount,
    selectedTab,
    setSelectedTab,
    formik,
    selectAll,
    selectAllFunc: customSelectAll,
    handleWorkshiftDelete,
    handleWorkshiftUpdate,
    canUpdateMultiple,
    hasMultiDaySelected,
    selectedWorkshiftCount,
    handleFilterReset,
    selectableData,
    order,
    orderBy,
    isDesc,
    handleSorting,
  };
};

export default useListOfWorkshifts;
